import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { fetchEvents, createEvent, setActiveEvent as apiSetActive, deactivateEvent as apiDeactivate } from '../lib/api';
import type { Event, EventFormData } from '../types';

// Common timezones for the dropdown
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

export function EventsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchEvents();
      setEvents(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  }

  const activeEvents = events.filter((e) => !e.archivedAt);
  const archivedEvents = events.filter((e) => e.archivedAt);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Event Administration</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            Create Event
          </button>
        </div>

        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-primary-200 border-t-primary-400 rounded-full animate-spin" />
            <p className="mt-2 text-gray-500">Loading events...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}

        {!isLoading && activeEvents.length === 0 && (
          <div className="text-center py-12 rounded-lg" style={{ background: '#FFFDF5' }}>
            <p className="text-gray-500">No events yet.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary mt-4"
            >
              Create your first event
            </button>
          </div>
        )}

        {activeEvents.length > 0 && (
          <section aria-labelledby="active-events-heading">
            <h2 id="active-events-heading" className="text-lg font-semibold text-gray-900 mb-4">
              Active Events
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {activeEvents.map((ev) => (
                <EventCard
                  key={ev.id}
                  event={ev}
                  onSetActive={async () => {
                    try {
                      await apiSetActive(ev.id);
                      loadEvents();
                    } catch {
                      // ignore
                    }
                  }}
                  onDeactivate={async () => {
                    try {
                      await apiDeactivate(ev.id);
                      loadEvents();
                    } catch {
                      // ignore
                    }
                  }}
                />
              ))}
            </div>
          </section>
        )}

        {archivedEvents.length > 0 && (
          <section aria-labelledby="archived-events-heading" className="mt-8">
            <h2 id="archived-events-heading" className="text-lg font-semibold text-gray-500 mb-4">
              Archived Events
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {archivedEvents.map((ev) => (
                <EventCard key={ev.id} event={ev} isArchived />
              ))}
            </div>
          </section>
        )}

        <CreateEventModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={async (data) => {
            const { id } = await createEvent(data);
            navigate(`/event/${id}/admin`);
          }}
        />
      </div>
    </Layout>
  );
}

function EventCard({ event, isArchived, onSetActive, onDeactivate }: { event: Event; isArchived?: boolean; onSetActive?: () => void; onDeactivate?: () => void }) {
  const formattedDate = new Date(event.startDate).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className={`card ${isArchived ? 'opacity-60' : ''}`}>
      <Link
        to={`/event/${event.id}`}
        className="block card-hover"
      >
        <h3 className="font-semibold text-gray-900">{event.name}</h3>
        <p className="text-sm text-gray-500 mt-1">{formattedDate}</p>
        <p className="text-xs text-gray-400 mt-1">{event.timezone}</p>
      </Link>
      <div className="flex items-center gap-2 mt-2">
        {event.isActive ? (
          <>
            <span className="badge badge-green">Active</span>
            {onDeactivate && (
              <button
                type="button"
                onClick={onDeactivate}
                className="btn btn-secondary btn-sm text-xs"
              >
                Deactivate
              </button>
            )}
          </>
        ) : !isArchived && onSetActive ? (
          <button
            type="button"
            onClick={onSetActive}
            className="btn btn-secondary btn-sm text-xs"
          >
            Set as Active
          </button>
        ) : null}
        {isArchived && (
          <span className="badge badge-gray">Archived</span>
        )}
      </div>
    </div>
  );
}

function CreateEventModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: EventFormData) => Promise<void>;
}) {
  const [formData, setFormData] = useState<EventFormData>({
    name: '',
    startDate: new Date().toISOString().split('T')[0],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    accessCode: '',
    organizerPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await onCreate(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Event"
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            form="create-event-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Event'}
          </button>
        </>
      }
    >
      <form id="create-event-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="name" className="label">
            Event Name *
          </label>
          <input
            id="name"
            type="text"
            className="input"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="startDate" className="label">
            Date *
          </label>
          <input
            id="startDate"
            type="date"
            className="input"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            required
          />
        </div>

        <div>
          <label htmlFor="timezone" className="label">
            Timezone
          </label>
          <select
            id="timezone"
            className="select"
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          >
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="accessCode" className="label">
            Event Access Code *
          </label>
          <input
            id="accessCode"
            type="text"
            className="input"
            value={formData.accessCode}
            onChange={(e) => setFormData({ ...formData, accessCode: e.target.value })}
            required
            placeholder="Shared with attendees for write access"
          />
          <p className="text-xs text-gray-500 mt-1">
            Share this code with attendees to allow them to submit session cards and show interest.
          </p>
        </div>

        <div>
          <label htmlFor="organizerPassword" className="label">
            Organizer Password *
          </label>
          <input
            id="organizerPassword"
            type="password"
            className="input"
            value={formData.organizerPassword}
            onChange={(e) => setFormData({ ...formData, organizerPassword: e.target.value })}
            required
            placeholder="For admin access"
          />
          <p className="text-xs text-gray-500 mt-1">
            Keep this secure - it grants full admin access to the event.
          </p>
        </div>
      </form>
    </Modal>
  );
}
